import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { coerceHttpParams } from 'src/app/shared/flight-log-api/shared/create-request-options';
import { AppState } from 'src/store';
import {
  DeleteTransmissionRun,
  UpsertTransmissionRun,
  UpsertTransmissionRuns,
} from 'src/store/transmission-run/transmission-run.actions';
import { TransmissionRunEntity } from 'src/store/transmission-run/transmission-run.model';
import { assertNotNullOrWhitespace } from 'src/util/values';
import { TransmissionRunViewModel } from 'src/view-models/transmission-run-view-model';
import { FlightLogApiConfiguration, FLIGHT_LOG_API_CONFIG } from '../flight-log-api-configuration';
import { CollectionResult } from '../flight-logs/flight-log-client-model-helpers';

export interface TransmissionRuns<T extends TransmissionRunEntity = TransmissionRunEntity>
  extends CollectionResult<T> {}

@Injectable({ providedIn: 'root' })
export class TransmissionRunHttpClient {
  constructor(
    @Inject(FLIGHT_LOG_API_CONFIG) private config: FlightLogApiConfiguration,
    private http: HttpClient,
    private _store: Store<AppState>,
  ) {}

  public create(transmissionRun: TransmissionRunViewModel): Observable<any> {
    return this.http.post(this._url(`transmissionRuns`), transmissionRun);
  }

  public update(transmissionRun: TransmissionRunViewModel): Observable<any> {
    return this.http.put(this._url(`transmissionRuns`), transmissionRun).pipe(
      tap(() => {
        this._store.dispatch(new UpsertTransmissionRun({ transmissionRun }));
      }),
    );
  }

  public delete(transmissionRunId: string): Observable<any> {
    assertNotNullOrWhitespace(transmissionRunId, 'transmissionRunId');
    return this.http
      .delete(this._url(`transmissionRuns/${transmissionRunId}/`))
      .pipe(tap(() => this._store.dispatch(new DeleteTransmissionRun({ id: transmissionRunId }))));
  }

  public getById(id: string): Observable<TransmissionRuns<TransmissionRunViewModel>> {
    assertNotNullOrWhitespace(id, 'TransmissionRunId');
    return this.http.get<TransmissionRuns>(this._url(`transmissionRuns/${id}`)).pipe(
      tap(({ results: transmissionRuns }) => {
        this._store.dispatch(new UpsertTransmissionRuns({ transmissionRuns }));
      }),
      map((res) => {
        return {
          results: res.results.map((r) => new TransmissionRunViewModel(r)),
          totalCount: res.totalCount,
        };
      }),
    );
  }

  public getByAirCarrierId(params: {
    airCarrierId: string;
    startTime?: string;
    endTime?: string;
    take?: number;
    skip?: number;
    searchTerm?: string;
  }): Observable<TransmissionRuns<TransmissionRunViewModel>> {
    return this.http
      .get<TransmissionRuns>(this._url('transmissionRuns'), {
        params: coerceHttpParams({
          ...params,
        }),
      })
      .pipe(
        tap(({ results: transmissionRuns }) => {
          this._store.dispatch(new UpsertTransmissionRuns({ transmissionRuns }));
        }),
        map((res) => {
          return {
            results: res.results.map((r) => new TransmissionRunViewModel(r)),
            totalCount: res.totalCount,
          };
        }),
      );
  }

  public getAll(
    params: {
      airCarrierId?: string;
      startTime?: string;
      endTime?: string;
      take?: number;
      skip?: number;
      searchTerm?: string;
    } = {},
  ): Observable<TransmissionRuns<TransmissionRunViewModel>> {
    return this.http
      .get<TransmissionRuns>(this._url('transmissionRuns'), {
        params: coerceHttpParams({
          ...params,
        }),
      })
      .pipe(
        tap(({ results: transmissionRuns }) => {
          this._store.dispatch(new UpsertTransmissionRuns({ transmissionRuns }));
        }),
        map((res) => {
          return {
            results: res.results.map((r) => new TransmissionRunViewModel(r)),
            totalCount: res.totalCount,
          };
        }),
      );
  }

  private _url(path: string) {
    return this.config.baseUri + '/api/' + path;
  }
}
